import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  IconButton,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import fittingsData from '../fittingsData';
import {
  findStraightCap,
  CIRCUIT_FITTING_CATEGORIES,
  THERMAL_POWER_INPUT_MIN_KW,
  getMaxThermalPowerKwCollectorCharacteristics,
  pickLargestCompatibleFittingIdInCategory,
} from './collectorUtils';
import {
  isFittingCompatibleWithMainPipe as isFloorCircuitFittingCompatibleRound,
  isFittingCompatibleWithSquareProfile as isFloorCircuitFittingCompatibleSquare,
  isEndFittingCompatibleWithMainPipe as isFloorEndFittingCompatibleRound,
  isEndFittingCompatibleWithSquareProfile as isFloorEndFittingCompatibleSquare,
} from './floorCollectorFittingCompatibility';
import {
  COLLECTOR_END_CONNECTION_CATEGORIES,
  COLLECTOR_END_CONNECTION_TYPE_AUTO_FALLBACK_ORDER,
  categoryLabelToConnectionType,
  filterCollectorEndConnectionSizes,
  pickOptimalConnectionSizeKey,
  velocityForConnectionSize,
  getFittingIdFromConnection,
  getTransitionPipeDiameter,
} from './endConnectionVelocitySizing';
import { getCapUpdatesForConnectionFitting } from './endConnectionsLogic';
import { getPipeDiameterForFitting } from './connectionLogic';
import {
  mergeFloorCircuitAutoSpacingToNext,
  getFloorCharacteristicsFirstLastOffsetsFromRows,
} from './floorCollectorCircuitSpacing';

// ─── Хелперы ──────────────────────────────────────────────────────────────────
const DEFAULT_FITTING_CATEGORY = 'Ниппели';
const DEFAULT_FITTING_ID       = 'Nippel_1';

const shortFittingLabel = (displayName) => {
  if (!displayName) return '—';
  const du = displayName.match(/Ду\s*(\d+)/);
  if (du) return `Ду${du[1]}`;
  const inch = displayName.match(/^[А-Яа-яA-Za-z\s]+?(\d[\d\s/]+")/);
  if (inch) {
    return inch[1]
      .trim()
      .replace('1/4', '¼')
      .replace('1/2', '½')
      .replace('3/4', '¾')
      .replace('3/8', '⅜');
  }
  const parts = displayName.split(' ');
  return parts.slice(1).join(' ') || displayName;
};

const getFittingsByCategory = (category, compatibilityFn = null) =>
  Object.entries(fittingsData)
    .filter(([, d]) => d.category === category)
    .map(([id, d]) => ({
      id,
      label: d.displayName,
      shortLabel: shortFittingLabel(d.displayName),
      diameter: d.diameter,
      isCompatible: compatibilityFn ? compatibilityFn(id) : true,
    }))
    .sort((a, b) => a.diameter - b.diameter);

const clampCircuitsToCompatibility = (circuits, isCompatible) =>
  circuits.map((c) => {
    if (!c.fitting || isCompatible(c.fitting)) return c;
    const cat = c.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    const best = pickLargestCompatibleFittingIdInCategory(cat, isCompatible);
    const nextId = best ?? c.fitting;
    return { ...c, fitting: nextId };
  });

const clampEndCircuitToCompatibility = (end, isCompatible, endConnectionCategory) => {
  if (!end?.fitting) return end;
  const cat = endConnectionCategory ?? end.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
  const catalogCategory = fittingsData[end.fitting]?.category;
  const categoryMismatch = catalogCategory != null && catalogCategory !== cat;
  if (isCompatible(end.fitting) && !categoryMismatch) return end;
  const best = pickLargestCompatibleFittingIdInCategory(cat, isCompatible);
  const nextId = best ?? end.fitting;
  return { ...end, fitting: nextId, fittingCategory: cat };
};

/** Как у распределительного: свободный торец относительно подключения источника. */
const endContourSlotVisibleLeft = (connectionSide) =>
  connectionSide !== 'left' && connectionSide !== 'both';
const endContourSlotVisibleRight = (connectionSide) =>
  connectionSide !== 'right' && connectionSide !== 'both';

const reconcileEndCircuitsOnConnectionSideChange = (prevSide, nextSide, left, right) => {
  const wasL = endContourSlotVisibleLeft(prevSide);
  const wasR = endContourSlotVisibleRight(prevSide);
  const nowL = endContourSlotVisibleLeft(nextSide);
  const nowR = endContourSlotVisibleRight(nextSide);

  if (wasR && !wasL && nowL && !nowR) {
    return { newLeft: right, newRight: null };
  }
  if (wasL && !wasR && nowR && !nowL) {
    return { newLeft: null, newRight: left };
  }

  if (nextSide === 'both') {
    return { newLeft: null, newRight: null };
  }

  return {
    newLeft: nowL ? left : null,
    newRight: nowR ? right : null,
  };
};

/** Старые сохранения: одно поле endCircuit → перенос в видимый слот. */
const migrateLegacyFloorEndCircuits = (ch) => {
  if (!ch || ch.endCircuit == null) return ch;
  if (ch.leftEndCircuit != null || ch.rightEndCircuit != null) return ch;
  const cs = ch.connectionSide ?? 'left';
  const legacy = ch.endCircuit;
  const { endCircuit: _e, ...rest } = ch;
  if (cs === 'left') return { ...rest, rightEndCircuit: legacy };
  if (cs === 'right') return { ...rest, leftEndCircuit: legacy };
  return { ...rest, rightEndCircuit: legacy };
};

let circuitIdCounter = 0;
const newCircuitId = () => `fc-${++circuitIdCounter}`;

function capDisplayLabel(shape, pressure, roundOuterMm) {
  if (shape === 'square') return 'Прямая (4/6 бар)';
  if (roundOuterMm != null && roundOuterMm > 60.3) return 'Эллиптическая (8/10 бар)';
  if (roundOuterMm != null && !findStraightCap(roundOuterMm)) return 'Эллиптическая (8/10 бар)';
  return Number(pressure) >= 8 ? 'Эллиптическая (8/10 бар)' : 'Прямая (6/8 бар)';
}

/**
 * Расчёт по характеристикам для этажного коллектора.
 *
 * Одна труба: «Верхний» ряд → circuits, «Нижний» → bottomCircuits.
 * Торцевые контуры (необязательно) — как у распределительного: слоты слева/справа
 * на свободных торцах относительно подключения источника.
 *
 * Формирует savedParams: { params, circuits, bottomCircuits, centerFittings }
 * для FloorCollectorEditor.
 */
function FloorCollectorCharacteristicsCalculator({ onBack, onCalculate, onBackToDrawing, initialParams }) {
  const lastInitialParamsRef = useRef(null);
  const shouldSaveToLocalStorage = useRef(true);

  const AVAILABLE_DIAMETERS_RAW = useMemo(() => {
    const { DEFAULT_PIPE_COMBOS } = require('../pipeCatalog');
    const excluded = [54, 306];
    const set = new Set();
    Object.keys(DEFAULT_PIPE_COMBOS).forEach((k) => {
      const [d] = k.split('|');
      set.add(parseFloat(d));
    });
    return Array.from(set)
      .filter((d) => d > 38 && !excluded.includes(d))
      .sort((a, b) => a - b)
      .map((d) => ({ value: d, label: `⌀${d}` }));
  }, []);

  const AVAILABLE_SQUARE_PROFILES = [
    { value: '40x40',   label: '40×40',   side: 40 },
    { value: '50x50',   label: '50×50',   side: 50 },
    { value: '60x60',   label: '60×60',   side: 60 },
    { value: '80x80',   label: '80×80',   side: 80 },
    { value: '100x100', label: '100×100', side: 100 },
    { value: '150x150', label: '150×150', side: 150 },
  ];
  const SQUARE_WALL_THICKNESS = { 40: 2, 50: 2, 60: 2, 80: 2, 100: 2, 150: 3 };
  const getSquareWallThickness = (side) => SQUARE_WALL_THICKNESS[side] ?? 2;

  const WALL_THICKNESS_MAP = {
    42.4: 2, 48.3: 2, 60.3: 2, 76.1: 2, 88.9: 2,
    101.6: 2, 114.3: 2, 129: 2, 139.7: 2, 159: 2,
    168.3: 3, 219.1: 3, 273: 3, 323.9: 3, 355.6: 3,
    406.4: 3, 508: 3, 609.6: 3,
  };
  const getRoundWallThickness = (od) => WALL_THICKNESS_MAP[od] ?? 2;

  const WATER_DENSITY = 1000;
  const WATER_SPECIFIC_HEAT = 4190;

  const calcFlowFromPower = (power, dT) => {
    if (!power || !dT) return 0;
    return ((power * 1000) / (WATER_DENSITY * WATER_SPECIFIC_HEAT * dT)) * 3600;
  };
  const calcPowerFromFlow = (flow, dT) => {
    if (!flow || !dT) return 0;
    return ((flow / 3600) * WATER_DENSITY * WATER_SPECIFIC_HEAT * dT) / 1000;
  };
  const velRound = (q, od, wt) => {
    if (!q || !od) return 0;
    const inner = od - 2 * wt;
    const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
    return (q / 3600) / area;
  };
  const velSquare = (q, side, wt) => {
    if (!q || !side) return 0;
    const inner = side - 2 * wt;
    const area = (inner / 1000) * (inner / 1000);
    return (q / 3600) / area;
  };

  const cleanupSaved = (obj) => {
    const c = { ...obj };
    delete c.fluid;
    if (!c.endConnectionCategory) c.endConnectionCategory = 'Ниппели';
    if (c.pressure == null || c.pressure === '') {
      c.pressure = c.shape === 'square' ? 4 : 6;
    } else {
      const p = Number(c.pressure);
      c.pressure = Number.isNaN(p) ? (c.shape === 'square' ? 4 : 6) : p;
    }
    return c;
  };

  const [characteristics, setCharacteristics] = useState(() => {
    const saved = initialParams?._characteristics;
    if (saved) {
      lastInitialParamsRef.current = initialParams;
      shouldSaveToLocalStorage.current = false;
      return cleanupSaved(migrateLegacyFloorEndCircuits(saved));
    }
    try {
      const raw = localStorage.getItem('floorCollectorCharacteristics');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.inputMode && parsed.deltaTemp) return cleanupSaved(migrateLegacyFloorEndCircuits(parsed));
      }
    } catch (e) { /* ignore */ }
    return {
      inputMode: 'power',
      power: 85,
      flowRate: 2.94,
      deltaTemp: 25,
      shape: 'round',
      collectorDiameter: null,
      collectorProfile: null,
      endConnectionCategory: 'Ниппели',
      endConnectionSize: null,
      connectionSide: 'left',
      pressure: 6,
    };
  });

  /** Подбор ⌀ гребёнки и поджим контуров — после blur, не при каждом символе. */
  const [committedThermal, setCommittedThermal] = useState(null);

  useLayoutEffect(() => {
    setCommittedThermal((prev) =>
      prev != null ? prev : { power: characteristics.power, flowRate: characteristics.flowRate }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeDefaultCircuit = (saved) => ({
    id: newCircuitId(),
    fittingCategory: saved?.fittingCategory ?? DEFAULT_FITTING_CATEGORY,
    fitting: saved?.fitting ?? DEFAULT_FITTING_ID,
  });

  const initCircuits = (savedList, count) => {
    if (savedList?.length > 0) return savedList.map(makeDefaultCircuit);
    return Array.from({ length: count ?? 3 }, () => makeDefaultCircuit());
  };

  // «Верхние» контуры (top / supply-аналог)
  const [topCircuits, setTopCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.topCircuits, initialParams?._characteristics?.circuitCount)
  );
  // «Нижние» контуры (bottom / return-аналог)
  const [bottomCircuits, setBottomCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.bottomCircuits, 0)
  );

  const makeDefaultEndCircuit = (saved) =>
    saved ? { fittingCategory: saved.fittingCategory ?? DEFAULT_FITTING_CATEGORY, fitting: saved.fitting ?? DEFAULT_FITTING_ID } : null;

  const initCharForEnds = migrateLegacyFloorEndCircuits(initialParams?._characteristics);

  const [leftEndCircuit, setLeftEndCircuit] = useState(() =>
    makeDefaultEndCircuit(initCharForEnds?.leftEndCircuit)
  );
  const [rightEndCircuit, setRightEndCircuit] = useState(() =>
    makeDefaultEndCircuit(initCharForEnds?.rightEndCircuit)
  );

  const leftEndCircuitRef = useRef(leftEndCircuit);
  const rightEndCircuitRef = useRef(rightEndCircuit);
  leftEndCircuitRef.current = leftEndCircuit;
  rightEndCircuitRef.current = rightEndCircuit;
  const prevConnectionSideForEndRef = useRef(characteristics.connectionSide);

  useEffect(() => {
    const next = characteristics.connectionSide;
    const prev = prevConnectionSideForEndRef.current;
    if (prev === next) return;
    prevConnectionSideForEndRef.current = next;

    const left = leftEndCircuitRef.current;
    const right = rightEndCircuitRef.current;
    const { newLeft, newRight } = reconcileEndCircuitsOnConnectionSideChange(prev, next, left, right);

    const sameLeft =
      (newLeft == null && left == null) ||
      (newLeft != null && left != null && newLeft.fitting === left.fitting && newLeft.fittingCategory === left.fittingCategory);
    const sameRight =
      (newRight == null && right == null) ||
      (newRight != null && right != null && newRight.fitting === right.fitting && newRight.fittingCategory === right.fittingCategory);
    if (!sameLeft) setLeftEndCircuit(newLeft);
    if (!sameRight) setRightEndCircuit(newRight);
  }, [characteristics.connectionSide]);

  const [circuitPopover, setCircuitPopover] = useState(null);

  const openCircuitPopover = (e, row, index = null) => {
    const el = e?.currentTarget ?? e?.target;
    const rect = el?.getBoundingClientRect();
    const anchorPosition = rect
      ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
      : null;
    let currentFittingCategory = DEFAULT_FITTING_CATEGORY;
    if (row === 'top') currentFittingCategory = topCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    else if (row === 'bottom') currentFittingCategory = bottomCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    else if (row === 'leftEnd') {
      currentFittingCategory = characteristics.endConnectionCategory
        ?? leftEndCircuit?.fittingCategory
        ?? DEFAULT_FITTING_CATEGORY;
    } else if (row === 'rightEnd') {
      currentFittingCategory = characteristics.endConnectionCategory
        ?? rightEndCircuit?.fittingCategory
        ?? DEFAULT_FITTING_CATEGORY;
    }
    setCircuitPopover({ anchorPosition, row, index, step: 'category', category: currentFittingCategory });
  };
  const closeCircuitPopover = () => setCircuitPopover(null);
  const selectCategory = (cat) => setCircuitPopover((prev) => ({ ...prev, step: 'fitting', category: cat }));
  const selectFitting = (fittingId) => {
    if (!circuitPopover) return;
    const { row, index, category } = circuitPopover;
    if (row === 'top') setTopCircuits((prev) => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    else if (row === 'bottom') setBottomCircuits((prev) => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    else if (row === 'leftEnd') setLeftEndCircuit({ fittingCategory: category, fitting: fittingId });
    else if (row === 'rightEnd') setRightEndCircuit({ fittingCategory: category, fitting: fittingId });
    closeCircuitPopover();
  };

  const addTopCircuit = () => setTopCircuits((prev) => [...prev, makeDefaultCircuit()]);
  const removeTopCircuit = (i) => setTopCircuits((prev) => prev.filter((_, idx) => idx !== i));
  const addBottomCircuit = () => setBottomCircuits((prev) => [...prev, makeDefaultCircuit()]);
  const removeBottomCircuit = (i) => setBottomCircuits((prev) => prev.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (initialParams?._characteristics) {
      const prev = lastInitialParamsRef.current?._characteristics;
      const next = migrateLegacyFloorEndCircuits(initialParams._characteristics);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(next)) {
        lastInitialParamsRef.current = initialParams;
        const merged = cleanupSaved(next);
        setCharacteristics(merged);
        setCommittedThermal({ power: merged.power, flowRate: merged.flowRate });
        if (next.topCircuits) setTopCircuits(next.topCircuits.map((c) => ({ ...c, id: newCircuitId() })));
        if (next.bottomCircuits) setBottomCircuits(next.bottomCircuits.map((c) => ({ ...c, id: newCircuitId() })));
        setLeftEndCircuit(makeDefaultEndCircuit(next.leftEndCircuit));
        setRightEndCircuit(makeDefaultEndCircuit(next.rightEndCircuit));
      }
    } else if (!initialParams) {
      lastInitialParamsRef.current = null;
    }
  }, [initialParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCommittedThermal({
      power: characteristics.power,
      flowRate: characteristics.flowRate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- не при каждом вводе кВт/м³·ч
  }, [characteristics.shape, characteristics.deltaTemp, characteristics.inputMode]);

  useEffect(() => {
    if (!shouldSaveToLocalStorage.current) { shouldSaveToLocalStorage.current = true; return; }
    try {
      localStorage.setItem('floorCollectorCharacteristics', JSON.stringify(characteristics));
    } catch (e) { /* ignore */ }
  }, [characteristics]);

  // ─── Физика ───────────────────────────────────────────────────────────────
  const actualFlowRate = useMemo(() => {
    const { inputMode, power, flowRate, deltaTemp } = characteristics;
    return inputMode === 'power' ? calcFlowFromPower(power, deltaTemp) : (flowRate || 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characteristics.inputMode, characteristics.power, characteristics.flowRate, characteristics.deltaTemp]);

  const charForCircuitSizing = useMemo(() => {
    if (committedThermal == null) return characteristics;
    return {
      ...characteristics,
      power:
        characteristics.inputMode === 'power' && committedThermal.power !== undefined
          ? committedThermal.power
          : characteristics.power,
      flowRate:
        characteristics.inputMode === 'flowRate' && committedThermal.flowRate !== undefined
          ? committedThermal.flowRate
          : characteristics.flowRate,
    };
  }, [characteristics, committedThermal]);

  const actualFlowRateForCircuits = useMemo(() => {
    const { inputMode, power, flowRate, deltaTemp } = charForCircuitSizing;
    return inputMode === 'power' ? calcFlowFromPower(power, deltaTemp) : (flowRate || 0);
  }, [charForCircuitSizing]);

  const isSquare = characteristics.shape === 'square';

  const filteredDiameters = useMemo(() =>
    AVAILABLE_DIAMETERS_RAW.filter(({ value }) => {
      const wt = getRoundWallThickness(value);
      const v = velRound(actualFlowRate, value, wt);
      return v >= 0.2 && v <= 0.8;
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actualFlowRate]);

  const filteredProfiles = useMemo(() =>
    AVAILABLE_SQUARE_PROFILES.filter(({ side }) => {
      const wt = getSquareWallThickness(side);
      const v = velSquare(actualFlowRate, side, wt);
      return v >= 0.2 && v <= 0.8;
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actualFlowRate]);

  const optimalDiameter = useMemo(() => {
    if (!filteredDiameters.length) return null;
    return filteredDiameters.reduce((best, d) => {
      const wt = getRoundWallThickness(d.value);
      const v = velRound(actualFlowRate, d.value, wt);
      const bWt = getRoundWallThickness(best.value);
      const bV = velRound(actualFlowRate, best.value, bWt);
      return Math.abs(v - 0.5) < Math.abs(bV - 0.5) ? d : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDiameters, actualFlowRate]);

  const optimalProfile = useMemo(() => {
    if (!filteredProfiles.length) return null;
    return filteredProfiles.reduce((best, p) => {
      const wt = getSquareWallThickness(p.side);
      const v = velSquare(actualFlowRate, p.side, wt);
      const bWt = getSquareWallThickness(best.side);
      const bV = velSquare(actualFlowRate, best.side, bWt);
      return Math.abs(v - 0.5) < Math.abs(bV - 0.5) ? p : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProfiles, actualFlowRate]);

  const selectedDiameter = useMemo(() => {
    if (isSquare) return null;
    return characteristics.collectorDiameter ?? optimalDiameter?.value ?? null;
  }, [isSquare, characteristics.collectorDiameter, optimalDiameter]);

  const selectedProfile = useMemo(() => {
    if (!isSquare) return null;
    return characteristics.collectorProfile ?? optimalProfile?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalProfile]);

  const filteredDiametersForCircuits = useMemo(() =>
    AVAILABLE_DIAMETERS_RAW.filter(({ value }) => {
      const wt = getRoundWallThickness(value);
      const v = velRound(actualFlowRateForCircuits, value, wt);
      return v >= 0.2 && v <= 0.8;
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- стабильные константы каталога
  [actualFlowRateForCircuits]);

  const filteredProfilesForCircuits = useMemo(() =>
    AVAILABLE_SQUARE_PROFILES.filter(({ side }) => {
      const wt = getSquareWallThickness(side);
      const v = velSquare(actualFlowRateForCircuits, side, wt);
      return v >= 0.2 && v <= 0.8;
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actualFlowRateForCircuits]);

  const optimalDiameterForCircuits = useMemo(() => {
    if (!filteredDiametersForCircuits.length) return null;
    return filteredDiametersForCircuits.reduce((best, d) => {
      const wt = getRoundWallThickness(d.value);
      const v = velRound(actualFlowRateForCircuits, d.value, wt);
      const bWt = getRoundWallThickness(best.value);
      const bV = velRound(actualFlowRateForCircuits, best.value, bWt);
      return Math.abs(v - 0.5) < Math.abs(bV - 0.5) ? d : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDiametersForCircuits, actualFlowRateForCircuits]);

  const optimalProfileForCircuits = useMemo(() => {
    if (!filteredProfilesForCircuits.length) return null;
    return filteredProfilesForCircuits.reduce((best, p) => {
      const wt = getSquareWallThickness(p.side);
      const v = velSquare(actualFlowRateForCircuits, p.side, wt);
      const bWt = getSquareWallThickness(best.side);
      const bV = velSquare(actualFlowRateForCircuits, best.side, bWt);
      return Math.abs(v - 0.5) < Math.abs(bV - 0.5) ? p : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProfilesForCircuits, actualFlowRateForCircuits]);

  const selectedDiameterForCircuits = useMemo(() => {
    if (isSquare) return null;
    return characteristics.collectorDiameter ?? optimalDiameterForCircuits?.value ?? null;
  }, [isSquare, characteristics.collectorDiameter, optimalDiameterForCircuits]);

  const selectedProfileForCircuits = useMemo(() => {
    if (!isSquare) return null;
    return characteristics.collectorProfile ?? optimalProfileForCircuits?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalProfileForCircuits]);

  const endConnectionCategoryForClamp = characteristics.endConnectionCategory ?? DEFAULT_FITTING_CATEGORY;

  useEffect(() => {
    if (isSquare) {
      if (!selectedProfileForCircuits) return;
      const circuitCompat = (id) => isFloorCircuitFittingCompatibleSquare(id, selectedProfileForCircuits);
      const endCompat = (id) => isFloorEndFittingCompatibleSquare(id, selectedProfileForCircuits);
      setTopCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      setBottomCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      setLeftEndCircuit((prev) => clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
      setRightEndCircuit((prev) => clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
      return;
    }
    if (selectedDiameterForCircuits == null) return;
    const circuitCompat = (id) => isFloorCircuitFittingCompatibleRound(id, selectedDiameterForCircuits);
    const endCompat = (id) => isFloorEndFittingCompatibleRound(id, selectedDiameterForCircuits);
    setTopCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
    setBottomCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
    setLeftEndCircuit((prev) => clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
    setRightEndCircuit((prev) => clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
  }, [
    isSquare,
    selectedDiameterForCircuits,
    selectedProfileForCircuits,
    endConnectionCategoryForClamp,
    topCircuits.length,
    bottomCircuits.length,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const endConnectionType = categoryLabelToConnectionType(characteristics.endConnectionCategory);

  const filteredEndConnectionSizes = useMemo(() => {
    const raw = filterCollectorEndConnectionSizes({
      actualFlowM3h: actualFlowRate,
      connectionType: endConnectionType,
      isSquare,
      manifoldRoundOd: !isSquare ? selectedDiameter : null,
      manifoldSquareProfile: isSquare ? selectedProfile : null,
    });
    const manifoldOk = isSquare ? Boolean(selectedProfile) : selectedDiameter != null;
    if (!manifoldOk) return raw;
    return raw.filter((s) => {
      const idSizeForRule =
        endConnectionType === 'flange' && s.isFlangeSize ? s.nominalDiameter : s.diameter;
      const fid = getFittingIdFromConnection(endConnectionType, idSizeForRule);
      if (!fid) return true;
      if (isSquare && selectedProfile) return isFloorEndFittingCompatibleSquare(fid, selectedProfile);
      return isFloorEndFittingCompatibleRound(fid, selectedDiameter);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFlowRate, endConnectionType, isSquare, selectedDiameter, selectedProfile]);

  const optimalEndConnectionSizeKey = useMemo(() => {
    if (!filteredEndConnectionSizes.length) return null;
    return pickOptimalConnectionSizeKey(filteredEndConnectionSizes, endConnectionType, actualFlowRate, 0.9);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEndConnectionSizes, endConnectionType, actualFlowRate]);

  const effectiveEndConnectionSizeKey = useMemo(() => {
    if (characteristics.connectionSide === 'none') return null;
    if (characteristics.endConnectionSize != null) return characteristics.endConnectionSize;
    return optimalEndConnectionSizeKey;
  }, [characteristics.connectionSide, characteristics.endConnectionSize, optimalEndConnectionSizeKey]);

  const needsEndConnection = characteristics.connectionSide !== 'none';

  const currentVelocity = useMemo(() => {
    if (!isSquare && selectedDiameter) {
      const wt = getRoundWallThickness(selectedDiameter);
      return velRound(actualFlowRate, selectedDiameter, wt);
    }
    if (isSquare && selectedProfile) {
      const prof = AVAILABLE_SQUARE_PROFILES.find((p) => p.value === selectedProfile);
      if (prof) return velSquare(actualFlowRate, prof.side, getSquareWallThickness(prof.side));
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSquare, selectedDiameter, selectedProfile, actualFlowRate]);

  const connectionVelocityDisplay = useMemo(() => {
    if (!needsEndConnection || effectiveEndConnectionSizeKey == null) return null;
    return velocityForConnectionSize(endConnectionType, effectiveEndConnectionSizeKey, actualFlowRate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEndConnection, effectiveEndConnectionSizeKey, endConnectionType, actualFlowRate]);

  const hasValidDiameter = isSquare ? Boolean(selectedProfile) : Boolean(selectedDiameter);

  const hasValidConnectionSelection =
    !needsEndConnection ||
    (effectiveEndConnectionSizeKey != null && filteredEndConnectionSizes.length > 0);

  const maxAvailablePowerKw = useMemo(
    () => getMaxThermalPowerKwCollectorCharacteristics(characteristics.shape, characteristics.deltaTemp),
    [characteristics.shape, characteristics.deltaTemp]
  );
  const maxFlowRateForPowerLimit = useMemo(() => {
    if (!characteristics.deltaTemp) return Number.POSITIVE_INFINITY;
    return calcFlowFromPower(maxAvailablePowerKw, characteristics.deltaTemp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxAvailablePowerKw, characteristics.deltaTemp]);

  const roundPipeNoSixEightPressure =
    !isSquare && selectedDiameter != null && (selectedDiameter > 60.3 || !findStraightCap(selectedDiameter));

  const bothSidesNeedConcentricReducer = useMemo(() => {
    if (isSquare || !selectedDiameterForCircuits || !needsEndConnection) return false;
    const shape = 'round';
    const pipeDiameter = selectedDiameterForCircuits;
    const connSide = characteristics.connectionSide;
    const sourceLeft = connSide === 'left' || connSide === 'both';
    const sourceRight = connSide === 'right' || connSide === 'both';
    const sizeKey = effectiveEndConnectionSizeKey;
    const fittingIdAuto = needsEndConnection && sizeKey ? getFittingIdFromConnection(endConnectionType, sizeKey) : '';

    const sideNeedsReducer = (side) => {
      const isL = side === 'left';
      const sourceHere = isL ? sourceLeft : sourceRight;
      const endCircuit = isL ? leftEndCircuit : rightEndCircuit;
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = sourceHere || hasEndCircuitHere;
      const connFitId = endCircuit?.fitting ?? (sourceHere ? fittingIdAuto : '');
      if (!isActive || !connFitId) return false;
      const capUpdates = getCapUpdatesForConnectionFitting(connFitId, pipeDiameter, side, 'supply', shape);
      const camel = isL ? 'supplyLeft' : 'supplyRight';
      return capUpdates[`${camel}CapElementType`] === 'reducer';
    };

    return sideNeedsReducer('left') && sideNeedsReducer('right');
  }, [
    isSquare,
    selectedDiameterForCircuits,
    needsEndConnection,
    characteristics.connectionSide,
    effectiveEndConnectionSizeKey,
    endConnectionType,
    leftEndCircuit,
    rightEndCircuit,
  ]);

  useEffect(() => {
    if (!bothSidesNeedConcentricReducer) return;
    setCharacteristics((prev) => (Number(prev.pressure) === 8 ? prev : { ...prev, pressure: 8 }));
  }, [bothSidesNeedConcentricReducer]);

  useEffect(() => {
    if (isSquare || selectedDiameter == null) return;
    if (!findStraightCap(selectedDiameter) || selectedDiameter > 60.3) {
      setCharacteristics((prev) => Number(prev.pressure) === 8 ? prev : { ...prev, pressure: 8 });
    }
  }, [isSquare, selectedDiameter]);

  useEffect(() => {
    if (!characteristics.collectorDiameter || isSquare) return;
    const ok = filteredDiametersForCircuits.some((d) => d.value === characteristics.collectorDiameter);
    if (!ok) setCharacteristics((prev) => ({ ...prev, collectorDiameter: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDiametersForCircuits, isSquare, characteristics.collectorDiameter]);

  useEffect(() => {
    if (!characteristics.collectorProfile || !isSquare) return;
    const ok = filteredProfilesForCircuits.some((p) => p.value === characteristics.collectorProfile);
    if (!ok) setCharacteristics((prev) => ({ ...prev, collectorProfile: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProfilesForCircuits, isSquare, characteristics.collectorProfile]);

  useEffect(() => {
    if (characteristics.endConnectionSize == null) return;
    const ok = filteredEndConnectionSizes.some((s) => s.diameter === characteristics.endConnectionSize);
    if (!ok) setCharacteristics((prev) => ({ ...prev, endConnectionSize: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEndConnectionSizes]);

  useEffect(() => {
    if (characteristics.connectionSide === 'none') return;
    const manifoldOk = isSquare ? Boolean(selectedProfile) : selectedDiameter != null;
    if (!manifoldOk) return;
    if (!(actualFlowRate > 0)) return;
    if (filteredEndConnectionSizes.length > 0) return;

    const currentType = categoryLabelToConnectionType(
      characteristics.endConnectionCategory ?? DEFAULT_FITTING_CATEGORY
    );

    for (const ct of COLLECTOR_END_CONNECTION_TYPE_AUTO_FALLBACK_ORDER) {
      if (ct === currentType) continue;
      const list = filterCollectorEndConnectionSizes({
        actualFlowM3h: actualFlowRate,
        connectionType: ct,
        isSquare,
        manifoldRoundOd: !isSquare ? selectedDiameter : null,
        manifoldSquareProfile: isSquare ? selectedProfile : null,
      });
      if (list.length > 0) {
        const row = COLLECTOR_END_CONNECTION_CATEGORIES.find((c) => c.connectionType === ct);
        const label = row?.id;
        if (!label) continue;
        setCharacteristics((prev) =>
          prev.endConnectionCategory === label
            ? prev
            : { ...prev, endConnectionCategory: label, endConnectionSize: null }
        );
        return;
      }
    }
  }, [
    characteristics.connectionSide,
    characteristics.endConnectionCategory,
    isSquare,
    selectedProfile,
    selectedDiameter,
    actualFlowRate,
    filteredEndConnectionSizes.length,
  ]);

  // ─── Обновление характеристик ─────────────────────────────────────────────
  const clampPowerAndFlowToMax = (next) => {
    const maxP = getMaxThermalPowerKwCollectorCharacteristics(next.shape, next.deltaTemp);
    if (next.inputMode === 'power' && typeof next.power === 'number' && next.power > maxP) next.power = maxP;
    if (next.inputMode === 'flowRate' && next.flowRate != null && next.deltaTemp) {
      const fp = calcPowerFromFlow(next.flowRate, next.deltaTemp);
      if (fp > maxP) next.flowRate = parseFloat(calcFlowFromPower(maxP, next.deltaTemp).toFixed(2));
    }
  };

  const updateCharacteristic = (key, value) => {
    setCharacteristics((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'inputMode' && value === 'flowRate') {
        const p = prev.power === '' ? THERMAL_POWER_INPUT_MIN_KW : Number(prev.power);
        next.flowRate = parseFloat(calcFlowFromPower(p, prev.deltaTemp).toFixed(2));
      }
      if (key === 'inputMode' && value === 'power') {
        next.power = parseFloat(calcPowerFromFlow(prev.flowRate, prev.deltaTemp).toFixed(2));
      }
      if (key === 'shape') {
        next.collectorDiameter = null;
        next.collectorProfile = null;
        next.endConnectionSize = null;
        next.pressure = value === 'square' ? 4 : 6;
        clampPowerAndFlowToMax(next);
      }
      if (key === 'deltaTemp') clampPowerAndFlowToMax(next);
      if (key === 'endConnectionCategory') next.endConnectionSize = null;
      if (key === 'pressure' && next.shape === 'round' && next.collectorDiameter != null) {
        const od = Number(next.collectorDiameter);
        if (!Number.isNaN(od) && (od > 60.3 || !findStraightCap(od)) && Number(value) === 6) next.pressure = 8;
      }
      if (key === 'collectorDiameter' && next.shape === 'round' && value != null) {
        const od = Number(value);
        if (!Number.isNaN(od) && (od > 60.3 || !findStraightCap(od)) && Number(next.pressure) === 6) next.pressure = 8;
      }
      return next;
    });
  };

  // ─── Отступы первого/последнего контура (свои правила этажного коллектора) ─
  const computeOffsets = () =>
    getFloorCharacteristicsFirstLastOffsetsFromRows(topCircuits, bottomCircuits);

  // ─── Расчёт параметров → передача в FloorCollectorEditor ─────────────────
  const calculateParameters = () => {
    if (!hasValidDiameter) return;
    if (needsEndConnection && effectiveEndConnectionSizeKey == null) return;

    const flangeStubLen = (fId) => fId?.startsWith('Flanec_') ? 100 : null;

    const sizeKey = effectiveEndConnectionSizeKey;
    const connType = endConnectionType;
    const categoryLabel = characteristics.endConnectionCategory;

    const fittingIdAuto = needsEndConnection && sizeKey ? getFittingIdFromConnection(connType, sizeKey) : '';
    const transitionOd = needsEndConnection && sizeKey
      ? getTransitionPipeDiameter(connType, sizeKey)
      : (isSquare ? 76.1 : selectedDiameterForCircuits);

    const pipeDiameter = isSquare ? 76.1 : selectedDiameterForCircuits;
    const shape = isSquare ? 'square' : 'round';

    const roundStraightCapAllowed =
      !isSquare && selectedDiameterForCircuits != null && selectedDiameterForCircuits <= 60.3 && Boolean(findStraightCap(selectedDiameterForCircuits));

    let capType = !isSquare
      ? (roundStraightCapAllowed && Number(characteristics.pressure) < 8 ? 'straight' : 'elliptical')
      : 'straight';

    const connSide = characteristics.connectionSide;
    const sourceLeft = connSide === 'left' || connSide === 'both';
    const sourceRight = connSide === 'right' || connSide === 'both';

    const mapSupplyCapKeysToFloor = (side, capUpdates) => {
      const isL = side === 'left';
      const pascal = isL ? 'SupplyLeft' : 'SupplyRight';
      const camel = isL ? 'supplyLeft' : 'supplyRight';
      const pref = isL ? 'Left' : 'Right';
      const lower = isL ? 'left' : 'right';
      const out = {};
      const hk = `has${pascal}Cap`;
      if (capUpdates[hk] !== undefined) out[`has${pref}Cap`] = capUpdates[hk];
      if (capUpdates[`${camel}CapElementType`] !== undefined) out[`${lower}CapElementType`] = capUpdates[`${camel}CapElementType`];
      if (capUpdates[`${camel}CapType`] !== undefined) out[`${lower}CapType`] = capUpdates[`${camel}CapType`];
      if (capUpdates[`${camel}Reducer`] !== undefined) out[`${lower}Reducer`] = capUpdates[`${camel}Reducer`];
      return out;
    };

    // Переход на торце источника не отменяет 6/8 бар: противоположный торец — прямая заглушка при допустимом ⌀

    const makeFloorPipeConn = (side, enabled) => {
      const isL = side === 'left';
      const p = side;
      const endCircuit = isL ? leftEndCircuit : rightEndCircuit;
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = enabled || hasEndCircuitHere;

      if (!isActive) {
        return {
          [`has${isL ? 'Left' : 'Right'}Connection`]: false,
          [`${p}ConnectionType`]: 'fitting',
          [`${p}ConnectionDiameter`]: isSquare ? 76.1 : selectedDiameterForCircuits,
          [`${p}ConnectionLength`]: null,
          [`${p}ConnectionFittingCategory`]: '',
          [`${p}ConnectionFitting`]: '',
          [`${p}ConnectionFittingLength`]: null,
        };
      }

      const connFittingId = endCircuit?.fitting ?? fittingIdAuto;
      const connFittingCategory = endCircuit?.fittingCategory ?? categoryLabel;
      const connPipeDiameter = (() => {
        const ef = endCircuit?.fitting;
        if (!ef) return transitionOd;
        if (
          ef.startsWith('Flanec_') ||
          ef.startsWith('Nak_Gaika_') ||
          ef.startsWith('Shaib_Nar_Rezba_')
        ) {
          return getPipeDiameterForFitting(ef) ?? pipeDiameter;
        }
        return fittingsData[ef]?.diameter ?? pipeDiameter;
      })();

      return {
        [`has${isL ? 'Left' : 'Right'}Connection`]: true,
        [`${p}ConnectionType`]: 'fitting',
        [`${p}ConnectionDiameter`]: connPipeDiameter,
        [`${p}ConnectionLength`]: flangeStubLen(connFittingId),
        [`${p}ConnectionFittingCategory`]: connFittingCategory,
        [`${p}ConnectionFitting`]: connFittingId,
        [`${p}ConnectionFittingLength`]: null,
      };
    };

    const makeFloorCap = (side, sourceHere) => {
      const isL = side === 'left';
      const pref = isL ? 'Left' : 'Right';
      const lower = isL ? 'left' : 'right';
      const endCircuit = isL ? leftEndCircuit : rightEndCircuit;
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = sourceHere || hasEndCircuitHere;
      const connFitId = endCircuit?.fitting ?? (sourceHere ? fittingIdAuto : '');

      if (!isActive || !connFitId) {
        return {
          [`has${pref}Cap`]: !isActive,
          [`${lower}CapElementType`]: 'cap',
          [`${lower}CapType`]: capType,
          [`${lower}Reducer`]: null,
        };
      }
      const capUpdates = getCapUpdatesForConnectionFitting(connFitId, pipeDiameter, side, 'supply', shape);
      if (!Object.keys(capUpdates).length) {
        return {
          [`has${pref}Cap`]: false,
          [`${lower}CapElementType`]: 'cap',
          [`${lower}CapType`]: capType,
          [`${lower}Reducer`]: null,
        };
      }
      const merged = {
        [`${lower}CapType`]: capType,
        [`${lower}Reducer`]: null,
        ...mapSupplyCapKeysToFloor(side, capUpdates),
      };
      const userStraight =
        shape === 'round' &&
        pipeDiameter <= 60.3 &&
        Boolean(findStraightCap(pipeDiameter)) &&
        Number(characteristics.pressure) < 8;
      if (
        merged[`${lower}CapElementType`] !== 'reducer' &&
        userStraight &&
        merged[`has${pref}Cap`] === true &&
        merged[`${lower}CapType`] === 'elliptical'
      ) {
        merged[`${lower}CapType`] = 'straight';
      }
      return merged;
    };

    const {
      firstCircuitOffset,
      lastCircuitOffset,
      bottomFirstCircuitOffset,
      bottomLastCircuitOffset,
    } = computeOffsets();
    const circuitSpacingVal = 100;

    const toFloorCircuit = (c, i) => ({
      id: i + 1,
      type: 'fitting',
      diameter: fittingsData[c.fitting]?.diameter ?? 26.9,
      length: 80,
      fittingCategory: c.fittingCategory,
      fittingId: c.fitting,
      fittingLength: flangeStubLen(c.fitting),
      spacingToNext: null,
    });

    const params = {
      shape,
      ...(isSquare ? { mainPipeProfile: selectedProfileForCircuits } : { mainPipeDiameter: selectedDiameterForCircuits }),

      circuitCount: topCircuits.length,
      firstCircuitOffset,
      firstCircuitOffsetMode: 'fromEdge',
      firstCircuitOffsetReferenceIndex: 0,
      firstCircuitOffsetSaved: null,
      firstCircuitOffsetLastUsed: 0,
      lastCircuitOffset,
      circuitSpacing: circuitSpacingVal,

      bottomCircuitCount: bottomCircuits.length,
      bottomFirstCircuitOffset,
      bottomFirstCircuitOffsetMode: 'fromEdge',
      bottomFirstCircuitOffsetReferenceIndex: 0,
      bottomFirstCircuitOffsetSaved: null,
      bottomFirstCircuitOffsetLastUsed: 0,
      bottomLastCircuitOffset,
      bottomCircuitSpacing: circuitSpacingVal,

      hasCenterFittings: false,
      centerFittingCount: 0,
      centerFittingSpacing: 100,

      ...makeFloorPipeConn('left', sourceLeft),
      ...makeFloorPipeConn('right', sourceRight),

      ...makeFloorCap('left', sourceLeft),
      ...makeFloorCap('right', sourceRight),

      useManualLength: false,
      manualLength: null,

      _calculatedFrom: 'characteristics',
      _characteristics: {
        ...characteristics,
        topCircuits: topCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        bottomCircuits: bottomCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        leftEndCircuit: leftEndCircuit
          ? { fittingCategory: leftEndCircuit.fittingCategory, fitting: leftEndCircuit.fitting }
          : null,
        rightEndCircuit: rightEndCircuit
          ? { fittingCategory: rightEndCircuit.fittingCategory, fitting: rightEndCircuit.fitting }
          : null,
      },
    };

    const circuits = mergeFloorCircuitAutoSpacingToNext(topCircuits.map(toFloorCircuit));
    const bottomCirArr = mergeFloorCircuitAutoSpacingToNext(bottomCircuits.map(toFloorCircuit));
    const centerFittings = [];

    onCalculate({ params, circuits, bottomCircuits: bottomCirArr, centerFittings });
  };

  // ─── Вспомогательные для рендера ──────────────────────────────────────────
  const displayFlowRate = actualFlowRate.toFixed(2);
  const displayPower = characteristics.inputMode === 'flowRate'
    ? calcPowerFromFlow(characteristics.flowRate, characteristics.deltaTemp).toFixed(2)
    : null;

  const collectorDiameterSelectValue =
    characteristics.collectorDiameter ?? optimalDiameter?.value ?? filteredDiameters[0]?.value ?? '';
  const collectorProfileSelectValue =
    characteristics.collectorProfile ?? optimalProfile?.value ?? filteredProfiles[0]?.value ?? '';
  const endConnectionSizeSelectValue =
    characteristics.endConnectionSize ?? optimalEndConnectionSizeKey ?? filteredEndConnectionSizes[0]?.diameter ?? '';

  // ─── Рендер ───────────────────────────────────────────────────────────────
  const LABEL_COL = 60;
  const ROW_COLOR = { top: '#1976d2', bottom: '#d32f2f', leftEnd: '#7b1fa2', rightEnd: '#7b1fa2' };
  const END_COLOR = '#7b1fa2';

  const renderChip = (circuit, index, row, onRemove) => {
    const fitFull   = fittingsData[circuit.fitting]?.displayName ?? circuit.fitting;
    const catLabel  = CIRCUIT_FITTING_CATEGORIES.find((c) => c.id === circuit.fittingCategory)?.label ?? circuit.fittingCategory;
    const chipLabel = index != null ? `${index + 1}. ${fitFull}` : fitFull;
    const chipColor = ROW_COLOR[row] ?? '#555';
    return (
      <Tooltip key={circuit.id ?? `${row}-${index}`} title={`${catLabel} / ${fitFull} — нажмите для изменения`}>
        <Chip
          label={chipLabel}
          size="small"
          onClick={(e) => openCircuitPopover(e, row, index)}
          onDelete={onRemove}
          deleteIcon={<DeleteIcon sx={{ fontSize: '0.8rem !important' }} />}
          sx={{
            cursor: 'pointer',
            height: 24,
            bgcolor: `${chipColor}18`,
            border: `1.5px solid ${chipColor}`,
            color: chipColor,
            fontWeight: 600,
            '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
            '& .MuiChip-deleteIcon': { ml: 0.25, mr: 0.5, color: chipColor, opacity: 0.7, '&:hover': { opacity: 1 } },
          }}
        />
      </Tooltip>
    );
  };

  const renderEndSlot = (side) => {
    const endCircuit = side === 'left' ? leftEndCircuit : rightEndCircuit;
    const setEnd = side === 'left' ? setLeftEndCircuit : setRightEndCircuit;
    const endRow = side === 'left' ? 'leftEnd' : 'rightEnd';
    const arrow = side === 'left' ? '←' : '→';
    const sideLabel = side === 'left' ? 'Левый торец' : 'Правый торец';
    return (
      <Box key={side} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: END_COLOR, fontWeight: 700, lineHeight: 1 }}>
          {sideLabel}
        </Typography>
        {endCircuit
          ? renderChip(endCircuit, null, endRow, () => setEnd(null))
          : (
            <Tooltip title={`Добавить торцевой контур (${side === 'left' ? 'слева' : 'справа'})`}>
              <IconButton size="small"
                onClick={(e) => {
                  const el = e.currentTarget;
                  const rect = el?.getBoundingClientRect();
                  const anchorPosition = rect
                    ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
                    : { top: 0, left: 0 };
                  setEnd({ fittingCategory: DEFAULT_FITTING_CATEGORY, fitting: DEFAULT_FITTING_ID });
                  setCircuitPopover({ anchorPosition, row: endRow, index: null, step: 'category', category: DEFAULT_FITTING_CATEGORY });
                }}
                sx={{
                  width: 28, height: 28,
                  bgcolor: `${END_COLOR}18`,
                  border: `1.5px dashed ${END_COLOR}`,
                  color: END_COLOR,
                  borderRadius: '6px',
                  '&:hover': { bgcolor: `${END_COLOR}30`, borderStyle: 'solid' },
                }}>
                <Typography variant="caption" sx={{ fontSize: '0.85rem', lineHeight: 1, fontWeight: 700 }}>{arrow}</Typography>
              </IconButton>
            </Tooltip>
          )
        }
      </Box>
    );
  };

  const popCur = circuitPopover
    ? (circuitPopover.row === 'top' ? topCircuits[circuitPopover.index]
      : circuitPopover.row === 'bottom' ? bottomCircuits[circuitPopover.index]
      : circuitPopover.row === 'leftEnd' ? leftEndCircuit
      : circuitPopover.row === 'rightEnd' ? rightEndCircuit
      : null)
    : null;

  const popCompatibilityFn = (() => {
    if (!circuitPopover) return null;
    if (circuitPopover.row === 'leftEnd' || circuitPopover.row === 'rightEnd') {
      return isSquare
        ? (selectedProfileForCircuits ? (id) => isFloorEndFittingCompatibleSquare(id, selectedProfileForCircuits) : null)
        : (selectedDiameterForCircuits ? (id) => isFloorEndFittingCompatibleRound(id, selectedDiameterForCircuits) : null);
    }
    return isSquare
      ? (selectedProfileForCircuits ? (id) => isFloorCircuitFittingCompatibleSquare(id, selectedProfileForCircuits) : null)
      : (selectedDiameterForCircuits ? (id) => isFloorCircuitFittingCompatibleRound(id, selectedDiameterForCircuits) : null);
  })();

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', py: 3 }}>
      <Container maxWidth="md">
        {/* Шапка */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton onClick={onBack} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}>
              <HomeIcon />
            </IconButton>
            {onBackToDrawing && (
              <IconButton onClick={onBackToDrawing} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Расчёт по характеристикам</Typography>
            <Typography variant="body2" color="text.secondary">Этажный коллектор</Typography>
          </Box>
        </Box>

        {/* Входные характеристики */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Входные характеристики</Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Режим ввода */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Режим ввода:</Typography>
                <ToggleButtonGroup
                  value={characteristics.inputMode}
                  exclusive
                  onChange={(_, v) => { if (v) updateCharacteristic('inputMode', v); }}
                  size="small"
                >
                  <ToggleButton value="power">По мощности</ToggleButton>
                  <ToggleButton value="flowRate">По расходу</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            {/* Сечение */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Сечение</InputLabel>
                <Select
                  value={characteristics.shape}
                  label="Сечение"
                  onChange={(e) => updateCharacteristic('shape', e.target.value)}
                >
                  <MenuItem value="round">Круглое</MenuItem>
                  <MenuItem value="square">Квадратное</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Разница температур */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                <Select
                  value={characteristics.deltaTemp}
                  label="Разница температур, °C (ΔT)"
                  onChange={(e) => updateCharacteristic('deltaTemp', e.target.value)}
                >
                  {[5, 10, 15, 20, 25].map((v) => (
                    <MenuItem key={v} value={v}>{v}°C</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Мощность или расход */}
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'power' ? (
                <TextField
                  label="Тепловая мощность, кВт"
                  type="number"
                  fullWidth
                  value={characteristics.power === '' ? '' : characteristics.power}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') { updateCharacteristic('power', ''); return; }
                    const n = parseFloat(v);
                    if (!isNaN(n)) updateCharacteristic('power', n);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    const fallback = Math.max(THERMAL_POWER_INPUT_MIN_KW, Math.min(85, maxAvailablePowerKw));
                    if (isNaN(n) || n <= 0) {
                      updateCharacteristic('power', fallback);
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), power: fallback }));
                    } else {
                      const clamped = Math.min(Math.max(n, THERMAL_POWER_INPUT_MIN_KW), maxAvailablePowerKw);
                      updateCharacteristic('power', clamped);
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), power: clamped }));
                    }
                  }}
                  inputProps={{ min: THERMAL_POWER_INPUT_MIN_KW, max: maxAvailablePowerKw, step: 1 }}
                  helperText={
                    !hasValidDiameter
                      ? 'Нет подходящих сечений коллектора (0.2–0.8 м/с) — измените ΔT'
                      : (needsEndConnection && !hasValidConnectionSelection
                        ? 'Нет размера торцевого фитинга в диапазоне 0.3–1.5 м/с'
                        : `От ${THERMAL_POWER_INPUT_MIN_KW} до ${maxAvailablePowerKw} кВт`)
                  }
                  error={!hasValidDiameter || (needsEndConnection && !hasValidConnectionSelection)}
                />
              ) : (
                <TextField
                  label="Расход теплоносителя, м³/ч"
                  type="number"
                  fullWidth
                  value={characteristics.flowRate === '' ? '' : characteristics.flowRate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') { updateCharacteristic('flowRate', ''); return; }
                    const n = parseFloat(v);
                    if (!isNaN(n)) updateCharacteristic('flowRate', n);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    const maxQ = maxFlowRateForPowerLimit;
                    if (isNaN(n) || n <= 0) {
                      const q = Math.min(1, maxQ);
                      updateCharacteristic('flowRate', q);
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), flowRate: q }));
                      return;
                    }
                    const qFinal = parseFloat(Math.min(Math.max(n, 0.1), maxQ).toFixed(2));
                    updateCharacteristic('flowRate', qFinal);
                    setCommittedThermal((prev) => ({ ...(prev ?? {}), flowRate: qFinal }));
                  }}
                  inputProps={{ min: 0.1, max: Number.isFinite(maxFlowRateForPowerLimit) ? maxFlowRateForPowerLimit : undefined, step: 0.1 }}
                  helperText={
                    !hasValidDiameter
                      ? 'Нет подходящих сечений коллектора (0.2–0.8 м/с) — измените ΔT'
                      : `Расход ограничен мощностью до ${maxAvailablePowerKw} кВт при ΔT = ${characteristics.deltaTemp}°C`
                  }
                  error={!hasValidDiameter}
                />
              )}
            </Grid>

            {/* Расчётный расход/мощность */}
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'power' ? (
                <TextField label="Рассчитанный расход, м³/ч" value={displayFlowRate} fullWidth disabled
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              ) : (
                <TextField label="Рассчитанная мощность, кВт" value={displayPower} fullWidth disabled
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              )}
            </Grid>

            {/* Давление */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Давление</InputLabel>
                <Select
                  value={characteristics.pressure}
                  label="Давление"
                  onChange={(e) => updateCharacteristic('pressure', Number(e.target.value))}
                >
                  <MenuItem value={4} sx={{ display: characteristics.shape === 'round' ? 'none' : 'block' }}>
                    4 бар рабочее / 6 бар испытательное
                  </MenuItem>
                  <MenuItem
                    value={6}
                    disabled={
                      characteristics.shape === 'square' ||
                      (characteristics.shape === 'round' &&
                        (roundPipeNoSixEightPressure || bothSidesNeedConcentricReducer))
                    }
                  >
                    6 бар рабочее / 8 бар испытательное
                    {characteristics.shape === 'round' &&
                      bothSidesNeedConcentricReducer &&
                      ' (оба торца — конц. переходы)'}
                  </MenuItem>
                  <MenuItem value={8} disabled={characteristics.shape === 'square'}>
                    8 бар рабочее / 10 бар испытательное
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Заглушка"
                value={
                  bothSidesNeedConcentricReducer
                    ? 'Концентрические переходы (8/10 бар)'
                    : capDisplayLabel(characteristics.shape, characteristics.pressure, !isSquare ? selectedDiameter : null)
                }
                fullWidth disabled InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
              />
            </Grid>

            {/* Диаметр / профиль */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}</InputLabel>
                {!isSquare ? (
                  <Select
                    value={collectorDiameterSelectValue}
                    label="Диаметр коллектора"
                    onChange={(e) => updateCharacteristic('collectorDiameter', Number(e.target.value))}
                  >
                    {filteredDiameters.map(({ value }) => {
                      const wt = getRoundWallThickness(value);
                      return (
                        <MenuItem key={value} value={value}>
                          ⌀{value} (внутр. ⌀{value - 2 * wt})
                        </MenuItem>
                      );
                    })}
                  </Select>
                ) : (
                  <Select
                    value={collectorProfileSelectValue}
                    label="Профиль коллектора"
                    onChange={(e) => updateCharacteristic('collectorProfile', e.target.value)}
                  >
                    {filteredProfiles.map(({ value, label, side }) => {
                      const wt = getSquareWallThickness(side);
                      return (
                        <MenuItem key={value} value={value}>
                          {label} (внутр. {side - 2 * wt}×{side - 2 * wt})
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </Grid>

            {/* Скорость */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Скорость в коллекторе, м/с"
                value={currentVelocity != null ? currentVelocity.toFixed(2) : '—'}
                fullWidth disabled InputProps={{ readOnly: true }}
                helperText="В корпусе гребёнки (0.2–0.8 м/с)"
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
              />
            </Grid>

            {/* Подключение источника */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  Подключение источника
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={needsEndConnection ? 4 : 12}>
                    <FormControl fullWidth>
                      <InputLabel>Торцевое подключение</InputLabel>
                      <Select
                        value={characteristics.connectionSide}
                        label="Торцевое подключение"
                        onChange={(e) => updateCharacteristic('connectionSide', e.target.value)}
                      >
                        <MenuItem value="left">Слева</MenuItem>
                        <MenuItem value="right">Справа</MenuItem>
                        <MenuItem value="both">С обеих сторон</MenuItem>
                        <MenuItem value="none">Нет подключений</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {needsEndConnection && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Категория</InputLabel>
                        <Select
                          value={characteristics.endConnectionCategory}
                          label="Категория"
                          onChange={(e) => updateCharacteristic('endConnectionCategory', e.target.value)}
                        >
                          {COLLECTOR_END_CONNECTION_CATEGORIES.map(({ id }) => (
                            <MenuItem key={id} value={id}>{id}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {needsEndConnection && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={needsEndConnection && !hasValidConnectionSelection}>
                        <InputLabel>Размер</InputLabel>
                        <Select
                          value={endConnectionSizeSelectValue}
                          label="Размер"
                          onChange={(e) => updateCharacteristic('endConnectionSize', parseFloat(e.target.value))}
                        >
                          {filteredEndConnectionSizes.map((s) => {
                            const v = velocityForConnectionSize(endConnectionType, s.diameter, actualFlowRate);
                            return (
                              <MenuItem key={s.diameter} value={s.diameter}>
                                {s.displayName}{v != null ? ` (${v.toFixed(2)} м/с)` : ''}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <FormHelperText>
                          {connectionVelocityDisplay != null
                            ? `Скорость в подключении: ${connectionVelocityDisplay.toFixed(2)} м/с (0.3–1.5)`
                            : 'Нет размеров в диапазоне 0.3–1.5 м/с'}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Контуры */}
        <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Контуры</Typography>
          </Box>
          <Divider />

          {[
            { row: 'top',    label: 'Верхний', circuits: topCircuits,    color: '#1976d2', bg: 'rgba(25,118,210,0.03)', addFn: addTopCircuit,    removeFn: removeTopCircuit },
            { row: 'bottom', label: 'Нижний',  circuits: bottomCircuits, color: '#d32f2f', bg: 'rgba(211,47,47,0.03)',  addFn: addBottomCircuit, removeFn: removeBottomCircuit },
          ].map(({ row, label, circuits, color, bg, addFn, removeFn }, rowIdx) => {
            const connSideStrip = characteristics.connectionSide;
            const showLeftEnd = endContourSlotVisibleLeft(connSideStrip);
            const showRightEnd = endContourSlotVisibleRight(connSideStrip);
            const hasAnyEnd = showLeftEnd || showRightEnd;
            return (
            <Box key={row}>
              {rowIdx === 1 && (
                <Box sx={{
                  display: 'flex', alignItems: 'center',
                  px: 2.5, py: 0.75, bgcolor: 'grey.50',
                  borderTop: '1px dashed', borderBottom: '1px dashed', borderColor: 'divider',
                }}>
                  <Box sx={{ width: LABEL_COL + 8 + 32, flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    {showLeftEnd && renderEndSlot('left')}
                    {hasAnyEnd && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem', fontStyle: 'italic', mx: 'auto' }}>
                        торцевые контуры (необязательно)
                      </Typography>
                    )}
                    {showRightEnd && renderEndSlot('right')}
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, px: 2.5, bgcolor: bg }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: LABEL_COL, flexShrink: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color, lineHeight: 1 }}>
                    {label}
                  </Typography>
                </Box>
                <Tooltip title="Добавить контур">
                  <IconButton size="small" onClick={addFn}
                    sx={{ bgcolor: color, color: 'white', width: 22, height: 22, flexShrink: 0, '&:hover': { opacity: 0.85 } }}>
                    <AddIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, ml: 4 }}>
                  {circuits.map((circuit, i) =>
                    renderChip(circuit, i, row, () => removeFn(i))
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                  {circuits.length} шт.
                </Typography>
              </Box>
            </Box>
            );
          })}
        </Paper>

        {/* Popover категория→фитинг */}
        <Popover
          open={Boolean(circuitPopover)}
          anchorReference="anchorPosition"
          anchorPosition={circuitPopover?.anchorPosition ?? { top: 0, left: 0 }}
          onClose={closeCircuitPopover}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 220, maxHeight: 320 } }}
        >
          {circuitPopover?.step === 'category' ? (
            <>
              <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Категория фитинга
              </Typography>
              <List dense disablePadding>
                {CIRCUIT_FITTING_CATEGORIES.map(({ id, label }) => (
                  <ListItem key={id} disablePadding>
                    <ListItemButton selected={popCur?.fittingCategory === id} onClick={() => selectCategory(id)} sx={{ px: 2, py: 0.6 }}>
                      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1 }}>
                <IconButton size="small" onClick={() => setCircuitPopover((prev) => prev ? { ...prev, step: 'category' } : prev)}>
                  <ArrowBackIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {CIRCUIT_FITTING_CATEGORIES.find((c) => c.id === circuitPopover?.category)?.label}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 240 }}>
                {getFittingsByCategory(circuitPopover?.category ?? DEFAULT_FITTING_CATEGORY, popCompatibilityFn).map(({ id, label, shortLabel, isCompatible }) => (
                  <ListItem key={id} disablePadding>
                    <ListItemButton
                      selected={popCur?.fitting === id}
                      onClick={() => isCompatible !== false && selectFitting(id)}
                      disabled={isCompatible === false}
                      sx={{ px: 2, py: 0.4, opacity: isCompatible === false ? 0.4 : 1 }}
                    >
                      <ListItemText
                        primary={shortLabel}
                        secondary={isCompatible === false ? `${label} — увеличьте Ø коллектора` : label}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption', color: isCompatible === false ? 'error' : 'text.secondary' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Popover>

        {/* Кнопка */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={calculateParameters}
            disabled={!hasValidDiameter || !hasValidConnectionSelection}
            sx={{ py: 1.5, px: 4, fontSize: '1.1rem', fontWeight: 600 }}
          >
            Рассчитать параметры
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default FloorCollectorCharacteristicsCalculator;
